import { UploadCloud } from 'lucide-react';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';
import { isPendingStageSemantic } from '@/utils/pipeline-stages';
import { emptyStateKeys } from './candidatesTable.utils';

type JobKey = ParseKeys<'job'>;

interface AddCvEmptyProps {
  titleKey: JobKey;
  onAddCv: () => void;
}

function AddCvEmpty({ titleKey, onAddCv }: AddCvEmptyProps) {
  const { t } = useTranslation('job');
  return (
    <button
      type="button"
      onClick={onAddCv}
      className="flex flex-col items-center gap-2.5 rounded-xl px-12 py-9 text-center transition-colors hover:bg-purple-soft/20"
    >
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-dark-invirtus-soft text-dark-invirtus/60">
        <UploadCloud className="h-6 w-6" />
      </span>
      <span className="text-sm font-semibold text-dark-invirtus font-body">
        {t(titleKey)}
      </span>
      <span className="text-xs text-dark-invirtus/55 font-body">
        {t('table.empty_add_cvs')}
      </span>
    </button>
  );
}

interface MessageEmptyProps {
  messageKey: JobKey;
  hintKey: JobKey;
}

function MessageEmpty({ messageKey, hintKey }: MessageEmptyProps) {
  const { t } = useTranslation('job');
  return (
    <div className="text-center flex flex-col items-center space-y-2">
      <p className="text-gray-500 font-body">{t(messageKey)}</p>
      <p className="text-gray-400 text-sm font-body">{t(hintKey)}</p>
    </div>
  );
}

interface CandidatesTableEmptyStatesProps {
  hasCandidates: boolean;
  filteredCount: number;
  /** Semantic of the active stage; undefined for custom / unknown stages. */
  activeStageType: string | undefined;
  searchTerm: string;
  onAddCv?: () => void;
}

export function CandidatesTableEmptyStates({
  hasCandidates,
  filteredCount,
  activeStageType,
  searchTerm,
  onAddCv,
}: CandidatesTableEmptyStatesProps) {
  if (!hasCandidates) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        {isPendingStageSemantic(activeStageType) && onAddCv ? (
          <AddCvEmpty titleKey="table.empty_no_candidates" onAddCv={onAddCv} />
        ) : (
          <MessageEmpty
            messageKey="table.empty_no_processed"
            hintKey="table.empty_soon"
          />
        )}
      </div>
    );
  }

  if (filteredCount > 0) return null;

  return (
    <div className="flex-1 flex items-center justify-center p-6">
      {!searchTerm && isPendingStageSemantic(activeStageType) && onAddCv ? (
        <AddCvEmpty titleKey="table.empty_no_pending" onAddCv={onAddCv} />
      ) : (
        <MessageEmpty
          {...emptyStateKeys(activeStageType, Boolean(searchTerm))}
        />
      )}
    </div>
  );
}
